import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { localStorageHelper } from '../../../../utils/localStorageHelper'

// Session state for the utility panel. Saved tiles are NOT held here
// (they live on viewConfig.defaultCharts via PortfolioDataProvider);
// this provider only owns the transient tile, the exclusive overlay,
// and panel visibility / layout.

const UtilityPanelContext = createContext(null)

export const LAYOUT_PRESETS = Object.freeze({
  sidebar: { contentFlex: 3, panelFlex: 1 },
  split: { contentFlex: 1, panelFlex: 1 },
  focus: { contentFlex: 1, panelFlex: 3 }
})

const VALID_LAYOUTS = Object.keys(LAYOUT_PRESETS)
const DEFAULT_LAYOUT = 'sidebar'

function layoutKey (userId) {
  return `portfolioExplorer.${userId}.utilityPanel.layoutMode`
}

function loadLayoutMode (userId) {
  if (userId == null) return DEFAULT_LAYOUT
  const stored = localStorageHelper.tryLoad(layoutKey(userId))
  return VALID_LAYOUTS.includes(stored) ? stored : DEFAULT_LAYOUT
}

let transientCounter = 0
function newTransientId () {
  transientCounter += 1
  return `transient_${transientCounter}`
}

export function UtilityPanelProvider ({ userId, children }) {
  // -- session tile state --
  const [transientTile, setTransientTile] = useState(null)
  const [exclusiveTool, setExclusiveTool] = useState(null)
  const [isOpen, setIsOpen] = useState(false)

  // -- layout --
  const [layoutMode, setLayoutModeState] = useState(() => loadLayoutMode(userId))
  // Session-only -- intentionally not persisted. Drag-resize is a quick
  // adjustment, not a setting; the user should always wake up in their
  // chosen preset.
  const [customRatio, setCustomRatio] = useState(null)

  // Legacy panel slots; only exclusive-mode tools (Research) write to
  // these. `toolBridge` is also used by Trend's Pattern B and reflects
  // the most-recently-mounted trend tile when multiple are open.
  const [headerMeta, setHeaderMetaState] = useState(null)
  const [actions, setActionsState] = useState(null)
  const [toolBridge, setToolBridgeState] = useState(null)

  const replaceTransientTile = useCallback(({ toolId, payload = null, catalogId = null } = {}) => {
    if (!toolId) return
    setTransientTile({ id: newTransientId(), toolId, payload, catalogId })
    setIsOpen(true)
  }, [])

  const closeTransientTile = useCallback(() => {
    setTransientTile(null)
  }, [])

  const openExclusive = useCallback((toolId, payload = null) => {
    if (!toolId) return
    setExclusiveTool({ toolId, payload })
    setIsOpen(true)
  }, [])

  const closeExclusive = useCallback(() => {
    setExclusiveTool(null)
    setHeaderMetaState(null)
    setActionsState(null)
    setToolBridgeState(null)
  }, [])

  // Back-compat shim. Always routes to the transient tile; exclusive-
  // mode callers (Research) must call openExclusive directly.
  const openTool = useCallback((toolId, payload = null) => {
    replaceTransientTile({ toolId, payload })
  }, [replaceTransientTile])

  // Hide the panel; tile + exclusive state is preserved.
  const closePanel = useCallback(() => {
    setIsOpen(false)
  }, [])

  const closeTool = closePanel

  const openPanel = useCallback(() => {
    setIsOpen(true)
  }, [])

  const setLayoutMode = useCallback((mode) => {
    if (!VALID_LAYOUTS.includes(mode)) return
    setLayoutModeState(mode)
    setCustomRatio(null)
    if (userId != null) {
      localStorageHelper.tryStore(layoutKey(userId), mode)
    }
  }, [userId])

  const setHeaderMeta = useCallback((meta) => {
    setHeaderMetaState(meta)
  }, [])

  const registerToolActions = useCallback((node) => {
    setActionsState(node)
  }, [])

  const setToolBridge = useCallback((bridge) => {
    setToolBridgeState(bridge)
  }, [])

  const effectivePreset = customRatio || LAYOUT_PRESETS[layoutMode] || LAYOUT_PRESETS[DEFAULT_LAYOUT]

  const value = useMemo(() => ({
    isOpen,
    openPanel,
    closePanel,
    closeTool,
    layoutMode,
    setLayoutMode,
    customRatio,
    setCustomRatio,
    effectivePreset,
    transientTile,
    replaceTransientTile,
    closeTransientTile,
    exclusiveTool,
    openExclusive,
    closeExclusive,
    openTool,
    headerMeta,
    setHeaderMeta,
    actions,
    registerToolActions,
    toolBridge,
    setToolBridge
  }), [
    isOpen,
    openPanel,
    closePanel,
    closeTool,
    layoutMode,
    setLayoutMode,
    customRatio,
    effectivePreset,
    transientTile,
    replaceTransientTile,
    closeTransientTile,
    exclusiveTool,
    openExclusive,
    closeExclusive,
    openTool,
    headerMeta,
    setHeaderMeta,
    actions,
    registerToolActions,
    toolBridge,
    setToolBridge
  ])

  return (
    <UtilityPanelContext.Provider value={value}>
      {children}
    </UtilityPanelContext.Provider>
  )
}

UtilityPanelProvider.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node
}

export function useUtilityPanel () {
  const ctx = useContext(UtilityPanelContext)
  if (!ctx) {
    throw new Error('useUtilityPanel must be used within a UtilityPanelProvider')
  }
  return ctx
}
